import type { SportEvent } from '../../types/SportEventTypes';

/**
 * Derive NBA player shifts from the substitution stream.
 *
 * The SES basketball feed never emits `ShiftEvent`s the way hockey does — it
 * only sends `basketball_substitution` events carrying a direction
 * (SUBSTITUTION_IN / SUBSTITUTION_OUT). The whole player-tab UI
 * (`usePlayerShifts`, `ShiftCard`, shift-anchored clip groups), however, is
 * built around `shift`-typed events shaped like the hockey `ShiftEvent`. This
 * module bridges the gap: it pairs IN→OUT substitutions into synthetic
 * `shift` SportEvents so the existing shift pipeline works for NBA with no
 * downstream changes.
 *
 * Product rules:
 *   - Starter inference — a player whose first event in a period is an OUT must
 *     have started that period on court, so we open their shift at the period
 *     start (otherwise opening stints would be lost for want of an IN).
 *   - Period boundaries — a still-on-court shift is closed at the quarter end
 *     (game clock 0) and a fresh shift is opened at the next period start for
 *     players who carry over without an explicit IN.
 *
 * Inherent limitation: a player with zero substitution events in a period
 * (started and was never subbed, with no sub events touching them at all)
 * can't be inferred from the sub stream alone and won't get a shift for that
 * period.
 *
 * NBA game-clock is a countdown (seconds remaining in the period), so
 * time-on-court for a shift is `game_clock_start - game_clock_end`.
 */

type RawPlayer = any;

interface OpenShift {
  player: RawPlayer;
  startNanos: unknown; // wall-clock nanoseconds (string | number)
  startClock: number; // game-clock seconds at start (countdown)
  period: number;
  shiftNumber: number;
}

// Period codes are band-encoded (see shared/utils/getPeriodName): 200-band =
// regulation quarters, 600-band = overtime. Regulation quarter = 12:00,
// overtime = 5:00 — used only to anchor the game-clock start of an inferred
// (starter / carry-over) shift, where no IN event provides one.
const OVERTIME_BAND = 600;
const QUARTER_LENGTH_SEC = 720;
const OVERTIME_LENGTH_SEC = 300;

function periodLengthSec(periodCode: number): number {
  return periodCode >= OVERTIME_BAND ? OVERTIME_LENGTH_SEC : QUARTER_LENGTH_SEC;
}

function playerId(player: RawPlayer): string | null {
  const main = player?.id?.main;
  return main == null ? null : String(main);
}

export function deriveNbaShifts(events: SportEvent[]): SportEvent[] {
  // Substitutions, chronological. `time` is wall-clock nanoseconds (string).
  const subs = events
    .filter((e) => (e.payload as any)?.type === 'substitution')
    .slice()
    .sort((a, b) => Number(a.time ?? 0) - Number(b.time ?? 0));

  if (subs.length === 0) return [];

  // Period anchors: prefer real `period_start` events (they carry both the
  // wall-clock tip-off time and the full game clock). When SES omits them (the
  // NBA feed currently never sends period_start), fall back to the EARLIEST
  // event of each period — any type, NOT just substitutions.
  //
  // Why earliest-event and not first-substitution: a starter / carry-over
  // player's shift inherits this wall-clock start, and usePlayerShiftDetail
  // buckets events into a shift by wall-clock (`time >= startTimeMs`). The
  // period's first substitution lands several minutes in, so anchoring there
  // silently drops every event before it — e.g. a starter's opening
  // baskets/free throws between tip-off and the first sub (they still count in
  // the game-total stat boxes, producing a list-vs-stats mismatch). The
  // earliest event is at-or-before every event in the period, so the shift
  // window covers them all.
  const periodStartNanos = new Map<number, unknown>();
  const periodStartClock = new Map<number, number>();
  for (const e of events) {
    if ((e.payload as any)?.type !== 'period_start') continue;
    const code = (e.clock as any)?.period;
    if (code == null) continue;
    periodStartNanos.set(code, e.time);
    const clk = Number((e.clock as any)?.time);
    if (Number.isFinite(clk) && clk > 0) periodStartClock.set(code, clk);
  }
  // Earliest event per period for the fallback. Compared via Number (fine at
  // the feed's millisecond granularity) but we keep the original `time` value
  // so no nanosecond precision is lost in the anchor we store.
  const earliestEventByPeriod = new Map<number, { raw: unknown; num: number }>();
  for (const e of events) {
    const code = (e.clock as any)?.period;
    if (code == null) continue;
    const num = Number(e.time ?? 0);
    if (!Number.isFinite(num)) continue;
    const cur = earliestEventByPeriod.get(code);
    if (cur === undefined || num < cur.num) earliestEventByPeriod.set(code, { raw: e.time, num });
  }
  for (const [code, { raw }] of earliestEventByPeriod) {
    if (!periodStartNanos.has(code)) periodStartNanos.set(code, raw);
  }

  const startNanosOf = (period: number): unknown => periodStartNanos.get(period) ?? null;
  const startClockOf = (period: number): number =>
    periodStartClock.get(period) ?? periodLengthSec(period);

  const open = new Map<string, OpenShift>();
  const shiftCounts = new Map<string, number>();
  const out: any[] = [];

  const emit = (s: OpenShift, endNanos: unknown, endClock: number): void => {
    const pid = playerId(s.player);
    if (!pid) return;
    const elapsed = Math.max(0, Math.round(s.startClock - endClock));
    const id = `shift_${pid}_${s.shiftNumber}`;
    out.push({
      id,
      sourceNativeId: id,
      time: s.startNanos,
      clock: { period: s.period, time: s.startClock },
      revision: { state: 1, version: 0 },
      timestampMs: s.startNanos,
      payload: {
        type: 'shift',
        data: {
          player: s.player,
          shift_number: s.shiftNumber,
          elapsed,
          start: s.startNanos,
          end: endNanos,
          game_clock_start: s.startClock,
          game_clock_end: endClock,
        },
      },
    });
  };

  const openShift = (
    player: RawPlayer,
    startNanos: unknown,
    startClock: number,
    period: number,
  ): void => {
    const pid = playerId(player);
    if (!pid) return;
    // Defensive: an IN with a still-open shift closes the previous one.
    const prev = open.get(pid);
    if (prev) emit(prev, startNanos, 0);
    const n = (shiftCounts.get(pid) ?? 0) + 1;
    shiftCounts.set(pid, n);
    open.set(pid, { player, startNanos, startClock, period, shiftNumber: n });
  };

  const closeShift = (pid: string, endNanos: unknown, endClock: number): void => {
    const s = open.get(pid);
    if (!s) return;
    emit(s, endNanos, endClock);
    open.delete(pid);
  };

  // Close every still-open shift at the end of the old period (game clock 0),
  // then re-open a fresh shift for those same players at the start of the new
  // period — that's how a player who isn't subbed carries into the next
  // quarter without an explicit IN.
  const crossPeriodBoundary = (toPeriod: number): void => {
    const boundaryNanos = startNanosOf(toPeriod);
    const carryOver: RawPlayer[] = [];
    for (const s of open.values()) carryOver.push(s.player);
    for (const pid of Array.from(open.keys())) closeShift(pid, boundaryNanos, 0);
    const newStartClock = startClockOf(toPeriod);
    for (const player of carryOver) openShift(player, boundaryNanos, newStartClock, toPeriod);
  };

  let currentPeriod: number | null = null;
  let lastNanos: unknown = null;

  for (const e of subs) {
    const period = (e.clock as any)?.period;
    const clock = Number((e.clock as any)?.time) || 0;
    const data = (e.payload as any)?.data;
    const direction = data?.direction;
    const player = data?.player;
    const pid = playerId(player);
    lastNanos = e.time;
    if (period == null || !pid) continue;

    if (currentPeriod != null && period !== currentPeriod) crossPeriodBoundary(period);
    currentPeriod = period;

    if (direction === 'SUBSTITUTION_IN') {
      openShift(player, e.time, clock, period);
    } else if (direction === 'SUBSTITUTION_OUT') {
      // Starter inference: an OUT with no open shift means the player started
      // the period on court — open retroactively at the period start.
      if (!open.has(pid)) openShift(player, startNanosOf(period) ?? e.time, startClockOf(period), period);
      closeShift(pid, e.time, clock);
    }
  }

  // End of game: close anything still open at the final whistle (clock 0).
  for (const pid of Array.from(open.keys())) closeShift(pid, lastNanos, 0);

  return out as SportEvent[];
}
